/**
 * Global multiplier composition. The breakdown is always derivable from
 * state — never store the composed total without its components, so the UI
 * can show players exactly where their boost comes from.
 *
 * Formula (`docs/09` §I.4 + `docs/14` upgrades integration):
 *   total = (1 + lessonsBonus)
 *         × (1 + atlasBonus)
 *         × (1 + upgradesBonus)
 *         × (1 + guidesBonus)
 *         × (1 + artifactSetsBonus)
 *         × (1 + roleBonus)
 *         × (1 + mantraBonus)
 *         × (1 + seasonBonus)
 *         × silentBlessing
 *         × prestige
 */
import type { MultiplierBreakdown } from './types.js';

export interface MultiplierInputs {
  readonly lessonsBonus?: number;
  readonly atlasBonus?: number;
  readonly upgradesBonus?: number;
  readonly guidesBonus?: number;
  readonly artifactSetsBonus?: number;
  readonly roleBonus?: number;
  readonly mantraBonus?: number;
  readonly seasonBonus?: number;
  readonly silentBlessing?: number;
  readonly prestige?: number;
}

/**
 * Compose the global multiplier and return both the breakdown and the total.
 * All bonus inputs are additive bonuses (0.1 = +10%); silentBlessing and
 * prestige are direct multipliers (1.1 = ×1.1).
 *
 * Negative inputs are clamped to 0 — we never let math accidentally penalize.
 */
export function composeGlobalMultiplier(inputs: MultiplierInputs): MultiplierBreakdown {
  const lessonsBonus = nonNegative(inputs.lessonsBonus ?? 0);
  const atlasBonus = nonNegative(inputs.atlasBonus ?? 0);
  const upgradesBonus = nonNegative(inputs.upgradesBonus ?? 0);
  const guidesBonus = nonNegative(inputs.guidesBonus ?? 0);
  const artifactSetsBonus = nonNegative(inputs.artifactSetsBonus ?? 0);
  const roleBonus = nonNegative(inputs.roleBonus ?? 0);
  const mantraBonus = nonNegative(inputs.mantraBonus ?? 0);
  const seasonBonus = nonNegative(inputs.seasonBonus ?? 0);
  const silentBlessing = Math.max(inputs.silentBlessing ?? 1, 1);
  const prestige = Math.max(inputs.prestige ?? 1, 1);

  const total =
    (1 + lessonsBonus) *
    (1 + atlasBonus) *
    (1 + upgradesBonus) *
    (1 + guidesBonus) *
    (1 + artifactSetsBonus) *
    (1 + roleBonus) *
    (1 + mantraBonus) *
    (1 + seasonBonus) *
    silentBlessing *
    prestige;

  return {
    lessonsBonus,
    atlasBonus,
    upgradesBonus,
    guidesBonus,
    artifactSetsBonus,
    roleBonus,
    mantraBonus,
    seasonBonus,
    silentBlessing,
    prestige,
    total,
  };
}

function nonNegative(n: number): number {
  return n < 0 || Number.isNaN(n) ? 0 : n;
}
